import { useEffect, useState, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import Header from './components/Header'
import Sidebar from './components/Sidebar'
import Map from './components/Map'
import LocationPanel from './components/LocationPanel'
import { CrimeFilters, LocationData } from './lib/types'

const allCrimeTypesOn = {
  homicidio_doloso: true,
  feminicidio: true,
  secuestro: true,
  extorsion: true,
  robo_vehiculo: true,
  robo_casa: true,
  robo_negocio: true,
  robo_transeunte: true,
  lesiones_dolosas: true,
  violencia_familiar: true,
  violacion: true,
  narcomenudeo: true,
}

const defaultFilters: CrimeFilters = {
  crimeTypes: { ...allCrimeTypesOn },
  timeRange: {
    startDate: '2024-01',
    endDate: '2025-01',
  },
  state: null,
}

const STATE_ABBR_TO_NAME: Record<string, string> = {
  AGU: 'Aguascalientes',
  BCN: 'Baja California',
  BCS: 'Baja California Sur',
  CAM: 'Campeche',
  CHP: 'Chiapas',
  CHH: 'Chihuahua',
  CMX: 'Ciudad de México',
  COA: 'Coahuila',
  COL: 'Colima',
  DUR: 'Durango',
  GUA: 'Guanajuato',
  GRO: 'Guerrero',
  HID: 'Hidalgo',
  JAL: 'Jalisco',
  MEX: 'México',
  MIC: 'Michoacán',
  MOR: 'Morelos',
  NAY: 'Nayarit',
  NLE: 'Nuevo León',
  OAX: 'Oaxaca',
  PUE: 'Puebla',
  QUE: 'Querétaro',
  ROO: 'Quintana Roo',
  SLP: 'San Luis Potosí',
  SIN: 'Sinaloa',
  SON: 'Sonora',
  TAB: 'Tabasco',
  TAM: 'Tamaulipas',
  TLA: 'Tlaxcala',
  VER: 'Veracruz',
  YUC: 'Yucatán',
  ZAC: 'Zacatecas',
}

const STATE_CODE_TO_NAME: Record<string, string> = {
  '01': 'Aguascalientes',
  '02': 'Baja California',
  '03': 'Baja California Sur',
  '04': 'Campeche',
  '05': 'Coahuila',
  '06': 'Colima',
  '07': 'Chiapas',
  '08': 'Chihuahua',
  '09': 'Ciudad de México',
  '10': 'Durango',
  '11': 'Guanajuato',
  '12': 'Guerrero',
  '13': 'Hidalgo',
  '14': 'Jalisco',
  '15': 'México',
  '16': 'Michoacán',
  '17': 'Morelos',
  '18': 'Nayarit',
  '19': 'Nuevo León',
  '20': 'Oaxaca',
  '21': 'Puebla',
  '22': 'Querétaro',
  '23': 'Quintana Roo',
  '24': 'San Luis Potosí',
  '25': 'Sinaloa',
  '26': 'Sonora',
  '27': 'Tabasco',
  '28': 'Tamaulipas',
  '29': 'Tlaxcala',
  '30': 'Veracruz',
  '31': 'Yucatán',
  '32': 'Zacatecas',
}

function resolveStateName(raw: string | null): string | null {
  if (!raw) return null
  const value = raw.trim()
  if (!value) return null

  const upper = value.toUpperCase()
  if (STATE_ABBR_TO_NAME[upper]) return STATE_ABBR_TO_NAME[upper]
  if (STATE_CODE_TO_NAME[value]) return STATE_CODE_TO_NAME[value]

  return value
}

// Check if we're on mobile (< 768px)
function getIsMobile() {
  if (typeof window === 'undefined') return false
  return window.innerWidth < 768
}

export default function App() {
  const location = useLocation()
  const [filters, setFilters] = useState<CrimeFilters>(defaultFilters)
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(null)
  const [sidebarOpen, setSidebarOpen] = useState(() => !getIsMobile())
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedMunicipalityId, setSelectedMunicipalityId] = useState<string | null>(null)

  // Close sidebar on mobile when resizing down
  useEffect(() => {
    const handleResize = () => {
      if (getIsMobile() && sidebarOpen) {
        setSidebarOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [sidebarOpen])

  const handleCrimeTypeToggle = useCallback((crimeType: string) => {
    setFilters(prev => ({
      ...prev,
      crimeTypes: {
        ...prev.crimeTypes,
        [crimeType]: !prev.crimeTypes[crimeType as keyof typeof prev.crimeTypes],
      },
    }))
  }, [])

  const handleLocationSelect = useCallback((location: LocationData | null) => {
    setSelectedLocation(location)
  }, [])

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query)
  }, [])

  const clearFilters = useCallback(() => {
    // If any filter is off, turn all on. If all are on, turn all off.
    const allOn = Object.values(filters.crimeTypes).every(v => v)
    const newCrimeTypes = Object.fromEntries(
      Object.keys(filters.crimeTypes).map(k => [k, !allOn])
    ) as CrimeFilters['crimeTypes']

    setFilters(prev => ({
      ...prev,
      crimeTypes: newCrimeTypes,
      state: null, // Also reset state filter
    }))
  }, [filters.crimeTypes])

  // Allow deep-links from /stats and /rankings.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const stateParam = resolveStateName(params.get('state'))
    const municipalityParam = params.get('municipality')

    setFilters((prev) => ({
      ...prev,
      state: stateParam,
    }))

    setSelectedMunicipalityId(municipalityParam)
  }, [location.search])

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <Header onSearch={handleSearch} />

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <Sidebar
          isOpen={sidebarOpen}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
          filters={filters}
          onCrimeTypeToggle={handleCrimeTypeToggle}
          onClearFilters={clearFilters}
        />

        {/* Map */}
        <div className="flex-1 relative">
          <Map
            filters={filters}
            searchQuery={searchQuery}
            selectedMunicipalityId={selectedMunicipalityId}
            onLocationSelect={handleLocationSelect}
          />

          {/* Mobile sidebar toggle */}
          {!sidebarOpen && (
            <button
              onClick={() => setSidebarOpen(true)}
              className="absolute top-4 left-4 z-10 bg-[var(--color-bg-card)] text-[var(--color-text)] px-4 py-2 rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-border)] transition-colors"
            >
              Filtros
            </button>
          )}
        </div>

        {/* Location Panel */}
        {selectedLocation && (
          <LocationPanel
            location={selectedLocation}
            onClose={() => setSelectedLocation(null)}
          />
        )}
      </div>
    </div>
  )
}
